/**
 * Avis clients.
 *
 * Deux points portent tout l'écran :
 *
 * - **Le texte d'un avis n'est pas du HTML.** La landing l'affiche comme du
 *   texte brut ; y coller du balisage l'afficherait littéralement, chevrons
 *   compris. D'où un champ de saisie simple, sans éditeur riche.
 * - **Un avis publié entre dans les données structurées.** Dès qu'il en existe
 *   un, `localBusinessJsonLd` calcule une note moyenne et la déclare aux
 *   moteurs. Publier un avis qui n'a pas été reçu, c'est déclarer une note
 *   fausse à Google, sous l'identité de l'entreprise.
 */
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Rating from "@mui/material/Rating";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ContenuAtelier,
  PiedFormulaire,
  Section,
  type Selection,
  champsModifies,
  erreurChamp,
  useFiche,
} from "../composants/ContenuAtelier";
import {
  type Avis,
  type SourceAvis,
  useAvis,
  useCommunes,
  useEnregistrer,
  useSupprimer,
} from "../composants/ContenuDonnees";

type Brouillon = {
  auteur: string;
  note: number;
  texte: string;
  source: SourceAvis;
  communeId: number | "";
  /** Format AAAA-MM-JJ attendu par l'API. Vide : l'API datera du jour. */
  date: string;
  isPublished: boolean;
};

const VIDE: Brouillon = {
  auteur: "",
  note: 5,
  texte: "",
  source: "manuel",
  communeId: "",
  date: "",
  isPublished: false,
};

const LIBELLES_SOURCE: Record<SourceAvis, string> = {
  google: "Google",
  manuel: "Recueilli directement",
};

function depuis(avis: Avis): Brouillon {
  return {
    auteur: avis.auteur,
    note: avis.note,
    texte: avis.texte,
    source: avis.source,
    communeId: avis.communeId ?? "",
    // L'API stocke parfois un horodatage complet ; le champ date n'en garde
    // que la partie calendaire, seule attendue au retour.
    date: avis.date.slice(0, 10),
    isPublished: avis.isPublished,
  };
}

function pourApi(champs: Record<string, unknown>): Record<string, unknown> {
  // La date est mise à part d'emblée : une date vide n'est pas une date, et il
  // faut l'**omettre** — l'envoyer vide ferait échouer la validation, alors que
  // son absence laisse l'API poser celle du jour.
  const { date, ...converti } = { ...champs };

  if ("communeId" in converti) {
    converti.communeId = converti.communeId === "" ? null : Number(converti.communeId);
  }
  if (date !== undefined && String(date) !== "") converti.date = date;

  return converti;
}

export function ContenuAvis() {
  const avis = useAvis();
  const communes = useCommunes();
  const enregistrement = useEnregistrer<Avis>("avis");
  const suppression = useSupprimer("avis");
  const fiche = useFiche<Brouillon>(VIDE);

  const liste = avis.data ?? [];
  const publies = liste.filter((element) => element.isPublished);
  const moyenne =
    publies.length === 0
      ? null
      : Math.round(
          (publies.reduce((total, element) => total + element.note, 0) / publies.length) * 10,
        ) / 10;

  function selectionner(cible: Selection) {
    enregistrement.reset();
    suppression.reset();

    if (cible === null) return fiche.fermer();
    if (cible === "nouveau") return fiche.ouvrir("nouveau", VIDE);

    const element = liste.find((candidat) => candidat.id === cible);
    if (element) fiche.ouvrir(cible, depuis(element));
  }

  function enregistrer() {
    const id = typeof fiche.selection === "number" ? fiche.selection : null;
    const champs =
      id === null ? { ...fiche.brouillon } : champsModifies(fiche.brouillon, fiche.reference);
    if (Object.keys(champs).length === 0) return;

    enregistrement.mutate(
      { id, champs: pourApi(champs) },
      {
        onSuccess: (element) => {
          if (element) fiche.confirmer(element.id, depuis(element));
        },
      },
    );
  }

  function supprimer() {
    if (typeof fiche.selection !== "number") return;
    suppression.mutate(fiche.selection, { onSuccess: () => fiche.fermer() });
  }

  const erreur = enregistrement.error ?? suppression.error ?? null;
  const brouillon = fiche.brouillon;

  return (
    <ContenuAtelier
      intro="Les avis publiés apparaissent sur le site et alimentent la note moyenne déclarée aux moteurs de recherche."
      entete={
        <Alert severity={publies.length === 0 ? "info" : "warning"}>
          {publies.length === 0
            ? "Aucun avis publié : aucune note moyenne n'est déclarée dans les données structurées, et le site l'annonce explicitement dans llms.txt."
            : `${publies.length} avis publié${publies.length > 1 ? "s" : ""}, note moyenne déclarée aux moteurs : ${moyenne}/5. N'y faites figurer que des avis réellement reçus : une note inventée est une fausse déclaration signée par l'entreprise.`}
        </Alert>
      }
      entrees={liste.map((element) => ({
        id: element.id,
        libelle: element.auteur,
        secondaire: `${element.note}/5 · ${LIBELLES_SOURCE[element.source]} · ${element.date.slice(0, 10)}`,
        publie: element.isPublished,
      }))}
      selection={fiche.selection}
      surSelection={selectionner}
      libelleNouveau="Nouvel avis"
      modifie={fiche.modifie}
      chargement={avis.isPending}
      erreur={avis.error ?? null}
      invite="Choisissez un avis à modifier, ou enregistrez-en un."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={4}>
          <Section titre="Avis">
            <TextField
              fullWidth
              label="Auteur"
              value={brouillon.auteur}
              onChange={(evenement) => fiche.modifier({ auteur: evenement.target.value })}
              error={erreurChamp(erreur, "auteur") !== undefined}
              helperText={
                erreurChamp(erreur, "auteur") ??
                "Tel que le client accepte d'être cité — prénom et initiale suffisent."
              }
            />

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" component="p" id="etiquette-note">
                Note
              </Typography>
              <Rating
                value={brouillon.note}
                aria-labelledby="etiquette-note"
                onChange={(_evenement, valeur) => fiche.modifier({ note: valeur ?? 1 })}
              />
              {erreurChamp(erreur, "note") && (
                <Typography variant="caption" color="error">
                  {erreurChamp(erreur, "note")}
                </Typography>
              )}
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Texte de l'avis"
              value={brouillon.texte}
              onChange={(evenement) => fiche.modifier({ texte: evenement.target.value })}
              error={erreurChamp(erreur, "texte") !== undefined}
              helperText={
                erreurChamp(erreur, "texte") ??
                "Texte brut, recopié tel que le client l'a écrit. Le site l'affiche sans mise en forme."
              }
            />
          </Section>

          <Section titre="Origine">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="etiquette-source">Source</InputLabel>
                <Select
                  labelId="etiquette-source"
                  label="Source"
                  value={brouillon.source}
                  onChange={(evenement) =>
                    fiche.modifier({ source: evenement.target.value as SourceAvis })
                  }
                >
                  {(Object.keys(LIBELLES_SOURCE) as SourceAvis[]).map((source) => (
                    <MenuItem key={source} value={source}>
                      {LIBELLES_SOURCE[source]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="date"
                label="Date de l'avis"
                value={brouillon.date}
                onChange={(evenement) => fiche.modifier({ date: evenement.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                error={erreurChamp(erreur, "date") !== undefined}
                helperText={erreurChamp(erreur, "date") ?? "Vide : la date du jour sera utilisée."}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="etiquette-commune-avis">Commune du chantier</InputLabel>
              <Select<number | "">
                labelId="etiquette-commune-avis"
                label="Commune du chantier"
                value={brouillon.communeId}
                onChange={(evenement) =>
                  fiche.modifier({
                    communeId: evenement.target.value === "" ? "" : Number(evenement.target.value),
                  })
                }
              >
                <MenuItem value="">Non précisée</MenuItem>
                {(communes.data ?? []).map((commune) => (
                  <MenuItem key={commune.id} value={commune.id}>
                    {commune.nom} ({commune.codePostal})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={brouillon.isPublished}
                  onChange={(evenement) =>
                    fiche.modifier({ isPublished: evenement.target.checked })
                  }
                />
              }
              label="Publier cet avis"
            />
            <Typography variant="caption" color="text.secondary">
              Publier cet avis le fait entrer dans la note moyenne déclarée aux moteurs de
              recherche.
            </Typography>
          </Section>
        </Stack>

        <PiedFormulaire
          modifie={fiche.modifie}
          enregistrement={enregistrement.isPending}
          surEnregistrer={enregistrer}
          surSupprimer={typeof fiche.selection === "number" ? supprimer : undefined}
          suppression={suppression.isPending}
          quoi="cet avis"
          erreur={erreur}
          succes={fiche.succes}
        />
      </Paper>
    </ContenuAtelier>
  );
}
